import { useCallback, useEffect, useRef, useState } from "react";

export interface RestTimerState {
  active: boolean;
  paused: boolean;
  remainingSeconds: number;
  totalSeconds: number;
}

export interface UseRestTimerResult extends RestTimerState {
  start: (seconds: number) => void;
  pause: () => void;
  resume: () => void;
  bump: (delta: number) => void;
  dismiss: () => void;
}

export function useRestTimer(
  onExpire?: () => void,
): UseRestTimerResult {
  const [state, setState] = useState<RestTimerState>({
    active: false,
    paused: false,
    remainingSeconds: 0,
    totalSeconds: 0,
  });
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!state.active || state.paused) return;
    const handle = window.setInterval(() => {
      setState((prev) => {
        if (!prev.active || prev.paused) return prev;
        const next = prev.remainingSeconds - 1;
        if (next <= 0) {
          onExpireRef.current?.();
          return {
            active: false,
            paused: false,
            remainingSeconds: 0,
            totalSeconds: prev.totalSeconds,
          };
        }
        return { ...prev, remainingSeconds: next };
      });
    }, 1000);
    return () => window.clearInterval(handle);
  }, [state.active, state.paused]);

  const start = useCallback((seconds: number) => {
    const safe = Math.max(0, Math.floor(seconds));
    setState({
      active: safe > 0,
      paused: false,
      remainingSeconds: safe,
      totalSeconds: safe,
    });
  }, []);

  const pause = useCallback(() => {
    setState((prev) => (prev.active ? { ...prev, paused: true } : prev));
  }, []);

  const resume = useCallback(() => {
    setState((prev) =>
      prev.active && prev.paused ? { ...prev, paused: false } : prev,
    );
  }, []);

  const bump = useCallback((delta: number) => {
    setState((prev) => {
      if (!prev.active) return prev;
      const next = Math.max(0, prev.remainingSeconds + delta);
      if (next === 0) {
        onExpireRef.current?.();
        return {
          active: false,
          paused: false,
          remainingSeconds: 0,
          totalSeconds: prev.totalSeconds,
        };
      }
      return { ...prev, remainingSeconds: next };
    });
  }, []);

  const dismiss = useCallback(() => {
    setState((prev) => ({
      active: false,
      paused: false,
      remainingSeconds: 0,
      totalSeconds: prev.totalSeconds,
    }));
  }, []);

  return { ...state, start, pause, resume, bump, dismiss };
}
